import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/context/AuthContext";
import DefaultLayout from "@/layouts/default";
import { clientsAPI, authAPI } from "@/services/api";

const CreateClientPage: React.FC = () => {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPassword, setClientPassword] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (clientPassword.length < 6) {
      setError(t("Password must be at least 6 characters long."));
      setLoading(false);

      return;
    }

    try {
      // Create a new user with email and password
      const userData = {
        email: clientEmail,
        password: clientPassword,
        role: "client",
      };

      // Register the user using the API
      const registeredUser = await authAPI.register(userData);

      // Create client record
      const clientData = {
        name: clientName,
        email: clientEmail,
        address: clientAddress,
        id: registeredUser.id,
        createdBy: user?.id,
        createdAt: new Date().toISOString(),
      };

      await clientsAPI.create(clientData);

      setLoading(false);
      navigate("/client");
    } catch (error: any) {
      console.error("Error adding client: ", error.message);
      setError(error.message || t("Failed to create client"));
      setLoading(false);
    }
  };

  return (
    <DefaultLayout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">{t("Create Client")}</h1>
        <form onSubmit={handleSubmit}>
          {error && <p className="text-red-500 mb-4">{error}</p>}
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="clientName"
            >
              {t("Client Name")}
            </label>
            <input
              required
              className="mt-1 p-2 block w-full border rounded-md"
              id="clientName"
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="clientEmail"
            >
              {t("Client Email")}
            </label>
            <input
              required
              className="mt-1 p-2 block w-full border rounded-md"
              id="clientEmail"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="clientPassword"
            >
              {t("Client Password")}
            </label>
            <input
              required
              className="mt-1 p-2 block w-full border rounded-md"
              id="clientPassword"
              minLength={6}
              type="password"
              value={clientPassword}
              onChange={(e) => setClientPassword(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="clientAddress"
            >
              {t("Client Address")}
            </label>
            <input
              required
              className="mt-1 p-2 block w-full border rounded-md"
              id="clientAddress"
              type="text"
              value={clientAddress}
              onChange={(e) => setClientAddress(e.target.value)}
            />
          </div>
          <button
            className="bg-blue-500 text-white p-2 rounded-md"
            disabled={loading}
            type="submit"
          >
            {loading ? t("Creating...") : t("Create Client")}
          </button>
        </form>
      </div>
    </DefaultLayout>
  );
};

export default CreateClientPage;
